import React from 'react'

import Link from 'next/link'
import {useRouter} from 'next/router'
import cn from 'classnames'
import range from 'lodash/range'
import {palette} from 'styled-tools'
import styled from 'styled-components'
import {useUpdateEffect} from 'react-use'
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon'
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon'

import {styledTheme} from '@festi/common/themes'

const Wrapper = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
`

const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const FlipLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  background-color: transparent;
  transition: background-color 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover {
    background-color: ${palette('ui5')};
  }
`

const PageLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${palette('blue')};
  border-top: 1px solid ${palette('ui20Solid')};
  border-bottom: 1px solid ${palette('ui20Solid')};
  border-left: 1px solid ${palette('ui20Solid')};
  border-radius: 0;
  background-color: ${palette('white')};
  transition: background-color 0.15s;

  &:first-of-type {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:last-of-type {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-right: 1px solid ${palette('ui20Solid')};
  }

  &.selected {
    background-color: ${palette('ui10Solid')};
    pointer-events: none;
  }

  &:hover {
    background-color: ${palette('ui5Solid')};
  }
`

const Spacer = styled.div`
  width: 10px;
  height: 20px;
  border-top: 1px solid ${palette('ui20Solid')};
  border-bottom: 1px solid ${palette('ui20Solid')};
  background-color: ${palette('light')};
`

interface Props {
  page?: number
  maxPage?: number
  pageMargin?: number
}

export default function Pagination({
  page,
  maxPage,
  pageMargin = 2,
}: Props): JSX.Element {
  const router = useRouter()

  const hasLess = page > 1
  const hasMore = page < maxPage

  const showMinEdge = page > pageMargin + 1
  const showMaxEdge = page < maxPage - pageMargin

  const min = Math.max(page - pageMargin, 1)
  const max = Math.min(page + pageMargin, maxPage)

  const linkProps = (p: number) => ({
    href: {
      pathname: router.pathname,
      query: {...router.query, page: p},
    },
    shallow: true,
    scroll: true,
  })

  useUpdateEffect(() => {
    if (page > maxPage) {
      router.replace({query: {...router.query, page: maxPage}})
    }
  }, [page, maxPage])

  return (
    <Wrapper aria-label="Síðuskipting">
      {hasLess && (
        <FlipLink {...linkProps(page - 1)} aria-label="Fyrri síða">
          <ArrowLeftSLineIcon
            size={24}
            color={styledTheme.palette.blue}
            aria-hidden="true"
          />
        </FlipLink>
      )}
      {showMinEdge && (
        <InnerWrapper>
          <PageLink {...linkProps(1)} aria-label="Fara á síðu 1">1</PageLink>
          <Spacer />
        </InnerWrapper>
      )}
      <InnerWrapper>
        {range(min, max + 1).map((p: number) => (
          <PageLink
            key={p}
            {...linkProps(p)}
            aria-label={`Fara á síðu ${p}`}
            aria-current={p === page ? 'page' : undefined}
            className={cn({ selected: p === page })}
          >
            {p}
          </PageLink>
        ))}
      </InnerWrapper>
      {showMaxEdge && (
        <InnerWrapper>
          <Spacer />
          <PageLink
            {...linkProps(maxPage)}
            aria-label={`Fara á síðu ${maxPage}`}
          >
            {maxPage}
          </PageLink>
        </InnerWrapper>
      )}
      {hasMore && (
        <FlipLink {...linkProps(page + 1)} aria-label="Næsta síða">
          <ArrowRightSLineIcon
            size={24}
            color={styledTheme.palette.blue}
            aria-hidden="true"
          />
        </FlipLink>
      )}
    </Wrapper>
  )
}
